import { PlayArrow, Refresh } from '@mui/icons-material'; // Import Refresh icon
import { Box, Drawer, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { RoomComponentsDrawerProps } from '../../../@types/ContextProviderRelated';
import { EElementDirection, EProgressType, EProgressVariant, ESubMenu, EWallMode } from '../../../@types/Enums';
import useResponsiveSizes from '../../../hooks/useResponsiveSizes';
import { useRoomComponentsData } from '../../../provider/RoomComponentsDataProvider';
import { useToolbarContext } from '../../../provider/ToolbarProvider';
import { immersightBlue } from '../../../utils/colors';
import { PRODUCT_GALLERY_DEFAULT_COLS } from '../../../utils/constants';
import ImsProgress from '../ImsProgress';
import ImsTabs from '../ImsTabs';
import RoomComponentsGallery from '../RoomComponentsGallery';

const RoomComponentsDrawer = (props: RoomComponentsDrawerProps): JSX.Element => {
  const { id, open, onClose } = props;
  const { setCurrentlyActiveSubMenu, setWallMode } = useToolbarContext();
  const sizes = useResponsiveSizes();

  // Get data from provider
  const {
    products: providerProducts,
    models2D: providerModels2D,
    loadCountTarget: providerLoadCountTarget,
    loadProgress: providerLoadProgress,
    isUserSearching,
    searchLoadProgress: providerSearchLoadProgress,
    searchLoadTarget: providerSearchLoadTarget,
    attributes: providerAttributes,
    categories: providerCategories,
    manufacturers: providerManufacturers,
    apiFetchCompleted,
    ensureAllProductsAndModels,
    refetchAllProductsAndModels,
    isFetchingProductsAndModels,
  } = useRoomComponentsData();

  // Local State
  const [isDrawerLoading, setIsDrawerLoading] = useState<boolean>(!apiFetchCompleted);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const hasRequestedInitialLoadRef = useRef(false);

  useEffect(() => {
    if (!open || hasRequestedInitialLoadRef.current) return;

    hasRequestedInitialLoadRef.current = true;
    void ensureAllProductsAndModels();
  }, [open, ensureAllProductsAndModels]);

  // Update loading state based on fetch progress and completion
  useEffect(() => {
    if (!apiFetchCompleted || (providerLoadCountTarget > 0 && providerLoadProgress < providerLoadCountTarget)) {
      setIsDrawerLoading(true);
    } else {
      setIsDrawerLoading(false);
    }
  }, [apiFetchCompleted, providerLoadProgress, providerLoadCountTarget]);

  // Close Drawer Handler
  const handleCloseDrawer = () => {
    setIsDrawerLoading(false);
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    setWallMode(EWallMode.NONE);
    onClose();
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: `${sizes.headerHeight}px`,
          zIndex: 1000,
          width: `340px`,
          height: `calc(100% - ${sizes.headerHeight}px)`,
          overflow: 'hidden',
        },
      }}
    >
      <Grid
        container
        direction="column"
        spacing={0.5}
        sx={{
          width: '96.5%',
          margin: '0',
        }}
      >
        {/* Drawer Header */}
        <Grid
          item
          sx={{
            width: '103.5%',
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="RoomComponentsDrawer.roomComponents" />
            </Typography>
          </Box>

          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>

        {/* Tabs Header */}
        <Grid
          item
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            marginLeft: 0.5,
            marginTop: 2,
            padding: 0,
          }}
        >
          <Grid item xs={11.5}>
            <ImsTabs tabs={[{ id: 'RoomComponentsDrawer.products' }, { id: 'RoomComponentsDrawer.models' }]} initialSelectedIndex={selectedTab} onTabChange={setSelectedTab} />
          </Grid>
          <Grid container justifyContent="center" alignItems="center">
            {isUserSearching && providerSearchLoadProgress !== providerSearchLoadTarget && providerSearchLoadTarget !== 0 ? (
              <ImsProgress
                variant={providerSearchLoadTarget > 2 ? EProgressVariant.determinate : EProgressVariant.indeterminate}
                direction={EElementDirection.LEFT_TO_RIGHT}
                type={EProgressType.circular}
                value={providerSearchLoadProgress}
                min={0}
                max={providerSearchLoadTarget}
                labelUnit={'%'}
                progressColor={`${immersightBlue}`}
                backgroundColor={'transparent !important'}
                style={{ pointerEvents: 'none' }}
              />
            ) : isDrawerLoading && providerLoadCountTarget !== 0 ? ( // Only show progress if actively loading
              <ImsProgress
                variant={EProgressVariant.determinate}
                direction={EElementDirection.LEFT_TO_RIGHT}
                type={EProgressType.circular}
                value={providerLoadProgress > 0 ? providerLoadProgress : 0}
                min={0}
                max={providerLoadCountTarget}
                showLabel={true}
                labelUnit={'%'}
                progressColor={`${immersightBlue}`}
                backgroundColor={'transparent !important'}
                style={{ pointerEvents: 'none' }}
              />
            ) : (
              // Show refresh button when not loading products/models
              !isFetchingProductsAndModels && (
                <Tooltip
                title={<FormattedMessage id="RoomComponentsDrawer.reloadTooltip" defaultMessage="Reload" />}
                arrow
              >
                <IconButton
                  onClick={refetchAllProductsAndModels}
                  sx={{
                    p: 0.5,
                    backgroundColor: immersightBlue,
                    color: '#fff',
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: immersightBlue },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              )
            )}
          </Grid>
        </Grid>

        {/* Room Components Gallery */}
        <RoomComponentsGallery
          attributes={providerAttributes}
          categories={providerCategories}
          manufacturers={providerManufacturers}
          products={providerProducts}
          models2D={providerModels2D}
          cols={PRODUCT_GALLERY_DEFAULT_COLS}
          selectedTab={selectedTab}
          styles={{
            height: `${sizes.innerWindowHeight - sizes.headerHeight * 2.9}px`,
            margin: '1.5rem .25rem',
          }}
        />
      </Grid>
    </Drawer>
  );
};

export default RoomComponentsDrawer;
